import { ScopedCssBaseline } from "@mui/material";
import Box from "@mui/material/Box";
import { Icon } from "components/Icon/Icon";
import { IconCollection } from "components/Icon/IconCollection";
import MUIDataTable, { MUIDataTableColumn, MUIDataTableOptions } from "mui-datatables";
import "./Table.scss";
import TablePlaceholder from "./TablePlaceholder";

const getDefaultOptions = ({
    isLoading,
    canSearchEverywhere,
}: {
    isLoading?: boolean;
    canSearchEverywhere?: boolean;
}) => {
    const options: MUIDataTableOptions = {
        filterType: "multiselect",
        download: false,
        print: false,
        elevation: 0,
        tableBodyMaxHeight: "100%",
        rowHover: !isLoading,
        textLabels: {
            body: {
                noMatch: isLoading ? (
                    <TablePlaceholder />
                ) : (
                    <div className="emptyTableInfo">
                        <Icon icon={IconCollection.packageVariant} key="emptyTableIcon" />
                        <div className="text" key="emptyTableText">
                            {canSearchEverywhere ? (
                                <>
                                    <div key={1}>Sorry, no matches found.</div>
                                    <div key={2}>Try removing filters to get results.</div>
                                </>
                            ) : (
                                <>
                                    <div key={1}>Sorry, there is no matching data to display </div>
                                    <div key={2}>
                                        or your access rights do not allow you to view any.
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                ),
            },
            pagination: {
                rowsPerPage: "Rows per page",
                jumpToPage: "Jump to page",
            },
        },
    };
    return options;
};

export interface TableProps<T> {
    title?: React.ReactNode;
    columns: MUIDataTableColumn[];
    data: T[];
    options?: MUIDataTableOptions;
    isLoading?: boolean;
    components?: any;
    canSearchEverywhere?: boolean;
    isDisabled?: boolean;
}

const ViewColumnsIcon = () => (
    <span className="iconButtonContainer">
        <span className="iconButton">
            <Icon icon={IconCollection.viewColumnOutline} />
        </span>
    </span>
);

export default function Table<T extends object>({
    title,
    columns,
    data,
    options,
    isLoading,
    components,
    canSearchEverywhere,
    isDisabled,
}: TableProps<T>) {
    return (
        <ScopedCssBaseline>
            <Box sx={{ width: "100%" }} className={`table ${isDisabled ? "disabledTable" : ""}`}>
                <MUIDataTable
                    title={title}
                    data={data}
                    columns={columns}
                    options={{
                        ...getDefaultOptions({ isLoading, canSearchEverywhere }),
                        ...options,
                    }}
                    components={{
                        ...components,
                        icons: { ViewColumnIcon: ViewColumnsIcon },
                    }}
                />
            </Box>
        </ScopedCssBaseline>
    );
}
